// Same as landing InactivityHandler
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getV2Token, clearV2Token } from '../../utils/v2AuthUtils';
import { logoutApi } from '../../services/userServicer';
import SecureCookieManager from '../../utils/secureCookie';

const InactivityHandler = () => {
  const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionStartTimeRef = useRef<number>(Date.now());
  const sessionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleLogout = async () => {
      try {
        const responseData = await logoutApi();
        if (responseData.success) {
          localStorage.clear();
          const domain = process.env.REACT_APP_DOMAIN || window.location.hostname;
          clearV2Token(domain);
          SecureCookieManager.clearAuthCookies();
          navigate('/login');
          toast("User logged out successfully", { type: "success" });
        } else {
          localStorage.clear();
          const domain = process.env.REACT_APP_DOMAIN || window.location.hostname;
          clearV2Token(domain);
          SecureCookieManager.clearAuthCookies();
          navigate("/login");
          toast("You logged out but session is still active, connect with admin", { type: "error" });
        }
      } catch (error: any) {
        localStorage.clear();
        const domain = process.env.REACT_APP_DOMAIN || window.location.hostname;
        clearV2Token(domain);
        SecureCookieManager.clearAuthCookies();
        navigate("/login");
        toast("You logged out but session is still active, connect with admin", { type: "error" });
      }
    };

    // Function to show warning toast 2 minutes before logout (same as landing)
    const showWarningToast = () => {
      toast.warning("Your session will expire in 2 minutes due to inactivity. Please save your work.", {
        autoClose: 120000, // 2 minutes
        position: 'top-center',
        toastId: 'session-warning' // Prevent duplicate warnings
      });
    };

    // Function to reset the inactivity timer (same as landing)
    const resetTimer = () => {
      // Clear existing timers
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }
      
      // Set warning timer (18 minutes = 20 minutes - 2 minutes warning) - same as landing
      sessionTimeoutRef.current = setTimeout(showWarningToast, 18 * 60 * 1000);
      
      // Set logout timer (20 minutes) - same as landing
      inactivityTimeoutRef.current = setTimeout(handleLogout, 20 * 60 * 1000);
    };

    // Set up event listeners to reset the timer on user interaction (same as landing)
    const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart', 'touchmove'];
    events.forEach(event => window.addEventListener(event, resetTimer, { passive: true }));

    // Start the inactivity timer when the component mounts
    resetTimer();

    // Cleanup event listeners and timeout on component unmount
    return () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }
      events.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, [navigate]);

  return null; // This component doesn't render anything
};

export default InactivityHandler;
